import type { RunObs } from "./model.js";

/**
 * The run duration for a forecast finalized by the resume/reattach path.
 *
 * A fresh submit measures `obs.latencyMs` from its in-memory stream start; a reload loses
 * that. The resume stream's `catchup` frame carries `elapsed_ms` (now − the row's
 * `started_at`, both on the SERVER clock); the caller stamps `receivedAt` (browser clock) when
 * the frame lands, so latency = elapsed_ms + (now − receivedAt) never mixes the two clocks.
 *
 * Only when the row was still RUNNING at attach: a run that finished while the tab was closed
 * completes "now" on this path, and now − started_at would be the closed-tab gap, not the run.
 * Returns `undefined` (keep whatever obs the run has) when no duration can be derived.
 */
export function resumedRunObs(
  prior: RunObs | null | undefined,
  catchup: { status?: unknown; elapsed_ms?: unknown; receivedAt?: unknown } | null | undefined,
  now: number,
): RunObs | undefined {
  if (!catchup || catchup.status !== "running") return undefined;
  const { elapsed_ms: elapsed, receivedAt } = catchup;
  if (typeof elapsed !== "number" || !Number.isFinite(elapsed) || elapsed < 0) return undefined;
  if (typeof receivedAt !== "number" || !Number.isFinite(receivedAt)) return undefined;
  const latencyMs = elapsed + Math.max(0, now - receivedAt);
  if (!(latencyMs > 0)) return undefined;
  return { ...prior, latencyMs };
}
